import * as React from 'react';
import { isNil } from 'lodash';
import currency from 'currency.js';

import Tooltip from 'components/Tooltip';
import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';

import CostAttributionTaskAmount from '../task-amount/CostAttributionTaskAmount';

import { __t, applyTranslations } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import {
  DELIVERABLE_ADDITIONAL_COSTS,
  DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO
} from 'utils/deliverables';
import { insertIf } from 'utils/list';
import { addThousandsSeparator } from 'utils/helper/common';
import { isFalsy, isGreaterThanZero, isMoneySum, isNotEmpty, isRequired, maxLength } from 'utils/validators';

import type { TextEntry } from 'types';

import './cost-attribution-tile-input.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./cost-attribution-tile-input.i18n.json'));

type Props = {
  type: string,
  mode?: 'budget' | 'percentage',
  canBeSkipped?: boolean,
  amount: number,
  value?: string,
  isExceededBudget: boolean,
  isBelowTheBudget: boolean,
  mustBeEmpty?: boolean,
  onChange: (newValue: string) => void
};

export const SUPPORTED_ATTRIBUTION_DELIVERABLES = [
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_ADDITIONAL_COSTS
];

const REQUIRED_DELIVERABLES = [
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO
];

function renderAmounts(type: string, amount: number): React.ReactNode {
  if (type === DELIVERABLE_ADDITIONAL_COSTS || type === DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION) {
    return <div />;
  }

  return (
    <div className="cost-attribution-tile-input">
      <CostAttributionTaskAmount
        type={type}
        amount={amount}
      />
    </div>
  );
}

function renderInfo(
  mode: 'budget' | 'percentage',
  type: string,
  valuePerTask: number
): React.ReactNode {
  if (type === DELIVERABLE_ADDITIONAL_COSTS || type === DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION) {
    return <div />;
  }

  return (
    <div className="cost-attribution-tile-input cost-attribution-tile-input__info">
      {__t('cost_attribution_tile_input.info_label')}
      {valuePerTask ? renderPrice(valuePerTask) : '–'}
    </div>
  );
}

const CostAttributionTileInput = (
  {
    type,
    amount,
    value,
    isExceededBudget,
    isBelowTheBudget,
    mustBeEmpty,
    onChange,
    canBeSkipped,
    mode = 'budget'
  }: Props
): JSX.Element => {
  const valuePerTask = currency(value ?? 0).divide(amount).value;
  const isTypeRequired = REQUIRED_DELIVERABLES.includes(type);
  const isSkippableField = !isTypeRequired && !value;

  let validators;
  if (!canBeSkipped) {
    validators = (mode === 'budget')
      ? [
        ...insertIf(isTypeRequired, isRequired(value, __t('cost_attribution_tile_input.missed_value_error'))),
        isMoneySum(value),
        isNotEmpty(!isExceededBudget, __t('cost_attribution_tile_input.budged_exceeded_error')),
        isNotEmpty(!isBelowTheBudget || isSkippableField, __t('cost_attribution_tile_input.budged_match_error'))
      ]
      : [
        ...insertIf(isTypeRequired, isRequired(value, __t('cost_attribution_tile_input.missed_value_error'))),
        ...insertIf(isTypeRequired, isGreaterThanZero(value)),
        isNotEmpty(!isExceededBudget, __t('cost_attribution_tile_input.percentage_exceeded_error')),
        isNotEmpty(!isBelowTheBudget || isSkippableField, __t('cost_attribution_tile_input.percentage_below_100_error'))
      ];
  }

  return (
    <React.Fragment>
      <div className="cost-attribution-tile-input cost-attribution-tile-input__type">
        {__t(`cost_attribution_tile_input.content_type.${type}`, { withPlural: amount })}
        {isTypeRequired && (
          <span className="cost-attribution-tile-input__require-mark">*</span>
        )}
        {type === DELIVERABLE_ADDITIONAL_COSTS && (
          <Tooltip
            showQuestionMark
            width="extra-long"
            classNameWrapper="cost-attribution-tile-input__tooltip"
            message={__t('cost_attribution_tile_input.additional_cost_tooltip')}
          />
        )}
      </div>

      {mode === 'budget' && renderAmounts(type, amount)}

      {type !== DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION && (
        <div>
          <FormField
            key={`${type}-${validators?.length}`}
            label=""
            direction="column"
            showRequiredMark
            validationErrorOverflow
            showValidationIcon={false}
            validationAlign="top"
            validators={validators}
            immediateValidationError={
              isExceededBudget && mode === 'budget'
                ? __t('cost_attribution_tile_input.budged_exceeded_error')
                : undefined
            }
          >
            <Input
              hint={mode === 'budget' ? '€' : '%'}
              hintAlign="right"
              align="right"
              value={
                String(isNil(value) ? '' : addThousandsSeparator(value))
              }
              onChange={(event: SyntheticEvent<TextEntry>) => {
                const val: string = event.currentTarget.value;
                const parsed = String(val).replace(/[^0-9.]/g, '');

                onChange(parsed);
              }}
            />
          </FormField>
        </div>
      )}
      {type === DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION && (
        <div className="cost-attribution-tile-input__additional-description">
          <FormField
            key={type}
            label=""
            direction="column"
            showRequiredMark
            validationErrorOverflow
            showValidationIcon={false}
            validationAlign="top"
            validators={[
              isFalsy(mustBeEmpty && Boolean(value), __t('cost_attribution_tile_input.description_must_be_empty')),
              isRequired(canBeSkipped || value, __t('cost_attribution_tile_input.missed_additional_description_error')),
              maxLength(value, 100)
            ]}
          >
            <Input
              value={isNil(value) ? '' : value}
              onChange={(event) => {
                const newValue = event.currentTarget.value ?? '';
                onChange(newValue);
              }}
              width="responsive"
              maxLength={100}
              multiline
              minMultilineHeight={33}
            />
          </FormField>
        </div>
      )}

      {renderInfo(mode, type, valuePerTask)}
    </React.Fragment>
  );
};

export default CostAttributionTileInput;
